import { createSelector } from 'reselect';

import { getClosestIntegerSum } from 'app/dealer/shared/utils/pure-functions/calculations';
import {
  selectCurrentLevel,
  selectCurrentDealerId,
} from 'app/dealer/shared/modules/dealersInsights/redux/level/selectors';
import { dealerInsightsDefaultCacheKey } from 'app/dealer/shared/modules/dealersInsights/api/cache';

import { getListingsBenchmarkReducer } from '../selectors';
import { selectSelectedListingsBenchmarkTimeDivisionFilter } from '../timeDivision/selectors';
import { listingsBenchmarkDataInitialState } from '../reducer.initialData';
import {
  selectListingsBenchmarkSelectedYear,
  selectListingsBenchmarkSelectedMonth,
} from '../general/selectors';

import { LISTINGS_PREFIX } from './constants';

/**
 *
 * @param {import('../reducer.defaultState').TimeDivision} selectedTimeDivisionFilter
 * @param {number} selectedYear
 * @param {number} selectedMonth
 * @param {import('../../../../../shared/modules/dealersInsights/redux/level/reducer.defaultState').DealerInsightsLevel} level
 * @param {string} dealerId
 */
export const getListingsBenchmarkCacheDictionaryKey = (
  selectedTimeDivisionFilter,
  selectedYear,
  selectedMonth,
  level,
  dealerId,
) =>
  dealerInsightsDefaultCacheKey({
    featurePrefix: LISTINGS_PREFIX,
    selectedTimeDivisionFilter,
    selectedYear,
    selectedMonth,
    level,
    dealerId,
  });

// #region select data by date and time division filters: main selector
/**
 * @description
 * main selector for selecting data corresponding to
 * cached dates in the redux store
 */
export const selectListingsBenchmarkDataByDate = createSelector(
  [
    getListingsBenchmarkReducer,
    selectSelectedListingsBenchmarkTimeDivisionFilter,
    selectListingsBenchmarkSelectedYear,
    selectListingsBenchmarkSelectedMonth,
    selectCurrentLevel,
    selectCurrentDealerId,
  ],
  (state, selectedTimeDivisionFilter, year, month, level, dealerId) =>
    state[
      getListingsBenchmarkCacheDictionaryKey(
        selectedTimeDivisionFilter,
        year,
        month,
        level,
        dealerId,
      )
    ] || listingsBenchmarkDataInitialState,
);
// #endregion select data by date and time division filters: main selector

// #region data properties: all previous and current
export const selectPreviousListingsBenchmarkDataValues = createSelector(
  [selectListingsBenchmarkDataByDate],
  ({ previous }) => previous,
);

export const selectCurrentListingsBenchmarkDataValues = createSelector(
  [selectListingsBenchmarkDataByDate],
  ({ current }) => current,
);
// #endregion data properties: all previous and current

// #region data properties: all are zero
export const selectAllListingsBenchmarkDataValuesAreZero = createSelector(
  [
    selectPreviousListingsBenchmarkDataValues,
    selectCurrentListingsBenchmarkDataValues,
  ],
  (previous, current) => {
    return [...Object.values(previous), ...Object.values(current)].every(
      val => val === 0,
    );
  },
);
// #endregion data properties: all are zero

// #region data properties: listings stats
export const selectCurrentActiveListings = createSelector(
  [selectCurrentListingsBenchmarkDataValues],
  ({ activeListings }) => activeListings,
);

export const selectPreviousActiveListings = createSelector(
  [selectPreviousListingsBenchmarkDataValues],
  ({ activeListings }) => activeListings,
);

export const selectCurrentFaveListings = createSelector(
  [selectCurrentListingsBenchmarkDataValues],
  ({ favsListings }) => favsListings,
);

export const selectPreviousFaveListings = createSelector(
  [selectPreviousListingsBenchmarkDataValues],
  ({ favsListings }) => favsListings,
);

export const selectCurrentClicksListings = createSelector(
  [selectCurrentListingsBenchmarkDataValues],
  ({ clicksListings }) => clicksListings,
);

export const selectPreviousClicksListings = createSelector(
  [selectPreviousListingsBenchmarkDataValues],
  ({ clicksListings }) => clicksListings,
);

export const selectCurrentSearchListings = createSelector(
  [selectCurrentListingsBenchmarkDataValues],
  ({ searchListings }) => searchListings,
);

export const selectPreviousSearchListings = createSelector(
  [selectPreviousListingsBenchmarkDataValues],
  ({ searchListings }) => searchListings,
);
// #endregion data properties: listings stats

// #region data properties: listings calculations
export const selectActiveListingsChange = createSelector(
  [selectCurrentActiveListings, selectPreviousActiveListings],
  (current, previous) => getClosestIntegerSum(current, previous * -1),
);

export const selectSearchListingsChange = createSelector(
  [selectCurrentSearchListings, selectPreviousSearchListings],
  (current, previous) => getClosestIntegerSum(current, previous * -1),
);

export const selectClicksListingsChange = createSelector(
  [selectCurrentClicksListings, selectPreviousClicksListings],
  (current, previous) => getClosestIntegerSum(current, previous * -1),
);

export const selectFaveListingsChange = createSelector(
  [selectCurrentFaveListings, selectPreviousFaveListings],
  (current, previous) => getClosestIntegerSum(current, previous * -1),
);
// #endregion data properties: listings stats

export const selectListingsTileCountUpDisabled = createSelector(
  [getListingsBenchmarkReducer],
  ({ listingTileCountupDisabled }) => listingTileCountupDisabled,
);
